import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {Object} BrandHighlightsRefs
 * @property {HTMLElement} track
 * @property {HTMLElement} [indicators]
 * @property {HTMLButtonElement[]} [indicatorButtons]
 */

/**
 * Mobile slide autoplay and indicators for brand highlights.
 * Desktop and stacked layouts are CSS-only.
 * @extends {Component<BrandHighlightsRefs>}
 */
class KanBrandHighlightsComponent extends Component {
  requiredRefs = ['track'];

  /** @type {number} */
  #index = 0;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number} */
  #scrollTimer = 0;

  /** @type {number | null} */
  #timer = null;

  /** @type {boolean} */
  #paused = false;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    this.#ensureIndicators();

    if (this.#items.length <= 1) return;

    const { signal } = this.#abortController;

    this.refs.track.addEventListener('scroll', this.#onScroll, { passive: true, signal });
    window.addEventListener('resize', this.#onResize, { passive: true, signal });
    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    document.addEventListener('visibilitychange', this.#onVisibilityChange, { signal });
    this.addEventListener('pointerenter', this.#pause, { signal });
    this.addEventListener('pointerleave', this.#resume, { signal });
    this.addEventListener('focusin', this.#pause, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });

    this.#sync();
    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * @param {Event} event
   */
  goTo(event) {
    event.preventDefault();
    if (!this.#isMobile()) return;

    const button = event.currentTarget;
    if (!(button instanceof HTMLElement)) return;

    const index = Number(button.dataset.index);
    if (Number.isNaN(index)) return;

    this.#scrollTo(index);
    this.#start();
  }

  get #items() {
    return Array.from(this.refs.track.children).filter((node) => node.nodeType === Node.ELEMENT_NODE);
  }

  /**
   * @returns {HTMLButtonElement[]}
   */
  get #indicatorButtons() {
    const wrap = this.refs.indicators;
    if (!wrap) return [];

    return Array.from(wrap.querySelectorAll('.kan-bh__indicator'));
  }

  get #interval() {
    const value = Number(this.dataset.interval);
    return Number.isFinite(value) && value >= 1000 ? value : 3000;
  }

  get #autoplay() {
    return this.dataset.autoplay === 'true' && !prefersReducedMotion();
  }

  #ensureIndicators() {
    const wrap = this.refs.indicators;
    if (!wrap) return;

    const count = this.#items.length;
    if (count <= 1) {
      wrap.hidden = true;
      return;
    }

    wrap.hidden = false;
    if (wrap.children.length === count) return;

    wrap.replaceChildren();
    const signal = this.#abortController?.signal;

    for (let i = 0; i < count; i++) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = i === 0 ? 'kan-bh__indicator is-active' : 'kan-bh__indicator';
      button.dataset.index = String(i);
      button.setAttribute('aria-label', `${i + 1} / ${count}`);
      if (i === 0) {
        button.setAttribute('aria-current', 'true');
      }
      button.addEventListener('click', (event) => this.goTo(event), signal ? { signal } : undefined);
      wrap.append(button);
    }
  }

  /**
   * @returns {boolean}
   */
  #isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  /**
   * @param {number} index
   */
  #scrollTo(index) {
    const item = this.#items[index];
    if (!item) return;

    this.refs.track.scrollTo({
      left: item.offsetLeft,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
    this.#index = index;
    this.#sync();
  }

  #advance() {
    if (!this.#isMobile() || this.#items.length <= 1) return;

    const next = (this.#index + 1) % this.#items.length;
    this.#scrollTo(next);
  }

  #start() {
    this.#stop();
    if (!this.#autoplay || this.#paused || !this.#isMobile()) return;
    if (this.#items.length <= 1) return;

    this.refs.track.setAttribute('aria-live', 'off');
    this.#timer = window.setTimeout(() => {
      this.#advance();
      this.#start();
    }, this.#interval);
  }

  #stop() {
    if (this.#timer) {
      window.clearTimeout(this.#timer);
      this.#timer = null;
    }
  }

  #pause = () => {
    this.#paused = true;
    this.refs.track.setAttribute('aria-live', 'polite');
    this.#stop();
  };

  #resume = () => {
    this.#paused = false;
    this.#start();
  };

  #onFocusOut = () => {
    if (this.contains(document.activeElement)) return;
    this.#resume();
  };

  #onVisibilityChange = () => {
    if (document.hidden) {
      this.#pause();
      return;
    }

    this.#resume();
  };

  #onScroll = () => {
    window.clearTimeout(this.#scrollTimer);
    this.#scrollTimer = window.setTimeout(() => {
      this.#indexFromScroll();
      this.#sync();
    }, 80);
  };

  #onResize = () => {
    this.#indexFromScroll();
    this.#sync();

    if (this.#isMobile()) {
      this.#start();
      return;
    }

    this.#stop();
  };

  /**
   * @param {Event} event
   */
  #onBlockSelect = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!this.contains(event.target)) return;

    const index = this.#items.findIndex((item) => item === event.target || item.contains(event.target));
    if (index < 0) return;

    this.#pause();
    this.#scrollTo(index);
  };

  #indexFromScroll() {
    const items = this.#items;
    if (!items.length) return;

    const left = this.refs.track.scrollLeft;
    let closest = 0;
    let min = Infinity;

    for (const [i, item] of items.entries()) {
      const dist = Math.abs(item.offsetLeft - left);
      if (dist < min) {
        min = dist;
        closest = i;
      }
    }

    this.#index = closest;
  }

  #sync() {
    const buttons = this.#indicatorButtons;
    if (!buttons.length) return;

    for (const [i, button] of buttons.entries()) {
      const active = i === this.#index;
      button.classList.toggle('is-active', active);
      if (active) {
        button.setAttribute('aria-current', 'true');
      } else {
        button.removeAttribute('aria-current');
      }
    }
  }
}

if (!customElements.get('kan-brand-highlights-component')) {
  customElements.define('kan-brand-highlights-component', KanBrandHighlightsComponent);
}
